import { useState, useEffect, useCallback } from "react";
import { TimerData, TimeLeft } from "../types/event.types";
import {
  calculateTimeLeft,
  isCheckInEnabled,
  isLessThanOneHourAway,
} from "../utils/eventUtils";

/**
 * Optimized timer hook that combines all timer-related calculations
 * Replaces multiple setInterval calls with a single optimized timer
 */
export const useEventTimer = (
  eventStartTime: string | undefined
): TimerData => {
  const [timerData, setTimerData] = useState<TimerData>({
    timeLeft: { days: 0, hours: 0, minutes: 0, seconds: 0 },
    checkInTimeLeft: { days: 0, hours: 0, minutes: 0, seconds: 0 },
    isCheckInEnabled: false,
    isLessThanOneHour: false,
  });

  useEffect(() => {
    if (!eventStartTime) return;

    const updateTimerData = () => {
      const now = Date.now();
      const eventTime = new Date(eventStartTime).getTime();
      const checkInTime = eventTime - 90 * 60 * 1000;

      const eventTimeDiff = eventTime - now;
      const timeLeft = calculateTimeLeft(eventTimeDiff);

      const checkInTimeDiff = checkInTime - now;
      const checkInTimeLeft = calculateTimeLeft(checkInTimeDiff);

      const checkInEnabled = now >= checkInTime && now <= eventTime;

      const lessThanOneHour = eventTimeDiff <= 3600000 && eventTimeDiff > 0;

      setTimerData({
        timeLeft,
        checkInTimeLeft,
        isCheckInEnabled: checkInEnabled,
        isLessThanOneHour: lessThanOneHour,
      });
    };

    updateTimerData();

    const interval = setInterval(updateTimerData, 1000);

    return () => clearInterval(interval);
  }, [eventStartTime]);

  return timerData;
};

/**
 * Hook for check-in specific logic
 */
export const useCheckInTimer = (eventStartTime: string | undefined) => {
  const { isCheckInEnabled: enabled, checkInTimeLeft } =
    useEventTimer(eventStartTime);

  const canCheckIn = useCallback(
    (eventStartTime: string | undefined): boolean => {
      if (!eventStartTime) return false;
      const currentTime = Date.now();
      const eventTime = new Date(eventStartTime).getTime();
      return enabled && currentTime <= eventTime;
    },
    [enabled]
  );

  return {
    isCheckInEnabled: enabled,
    checkInTimeLeft,
    canCheckIn: canCheckIn(eventStartTime),
  };
};
